// ---------------------------------------------------------------------------
// Package protection auto-sync
//
// Replaces the Loop Returns "return-coverage" app-embed block (now disabled
// in config/settings_data.json). Instead of an opt-in upsell, package
// protection is mandatory: as long as the cart has at least one real
// product line, a single non-removable coverage line item is kept in sync
// with Loop's Checkout+ pricing (https://docs.loopreturns.com/integration-
// guides/checkout-plus), called directly from the browser (Cart-scoped key).
//
// Loop's coverage product ("free-returns-package-protection") is set up as
// ~66 discrete variants, one per fee amount, rather than a single variant
// priced per unit or a continuously-priced line (confirmed live — adding a
// variant returns exactly its nominal price, no Cart Transform Function
// rewrites it) — so it can only ever approximate Loop's computed fee to the
// nearest tier. The theme renders the coverage line's own real price
// (sections/cart-drawer.liquid, snippets/cart-drawer-item.liquid), not
// Loop's theoretical exact figure — an earlier version showed the latter
// and it didn't match what checkout actually charges, since checkout
// charges the real variant price, not Loop's ideal.
//
// Triggered by the "cart:updated" / "cart:item-added" events (dispatched by
// cart-drawer.js and the PDP add-to-cart flows) plus once on load, debounced
// on the trailing edge and run on a serial queue so a burst of triggers
// collapses into a single sync against the final cart state instead of
// several overlapping/racing ones.
// ---------------------------------------------------------------------------

const LOOP_ANALYZE_URL = 'https://api.loopreturns.com/storefront/v1/analyze';

function isProtectionLine(item) {
  return !!(item.properties && item.properties._mode);
}

function cartDrawerEl() {
  return document.querySelector('cart-drawer');
}

function sectionsPayload() {
  const cartDrawer = cartDrawerEl();
  if (!cartDrawer) return { sections: [], sections_url: window.location.pathname };
  return {
    sections: cartDrawer.getSectionsToRender().map((section) => section.section),
    sections_url: window.location.pathname,
  };
}

// generationAtStart, when passed, guards against this render landing after
// (and reverting) a newer cart-drawer.js action — see the comment on
// generationAtStart in syncProtection for the full rationale.
function renderSections(state, generationAtStart) {
  const cartDrawer = cartDrawerEl();
  if (!cartDrawer || !state || !state.sections) return;
  if (generationAtStart != null && (window.__cartRenderGeneration || 0) !== generationAtStart) return;
  cartDrawer.getSectionsToRender().forEach((section) => {
    renderCartSection(section, state.sections[section.section]);
  });
}

// Every mutating call here throws on a non-OK response instead of
// silently continuing. This matters most for the removal calls: without
// it, a transient failure (rate limiting under rapid add/remove clicking,
// a network blip) would still let the *next* step in a sequence run —
// e.g. adding a new coverage variant after a removal that never actually
// happened — which is exactly how a duplicate line gets created. Throwing
// aborts the whole sync (caught by enqueueSync's .catch()); the next
// trigger re-reads the real cart state and retries cleanly.
async function checkedFetch(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`[CartProtectionSync] ${url} failed: ${res.status}`);
  return res;
}

async function removeLine(key) {
  await checkedFetch(window.routes.cart_change_url, { id: key, quantity: 0 });
}

// Combines removing a line with setting attributes in one request — both
// are accepted by /cart/update.js in a single call, which is what lets the
// tier-change path (see syncProtection) get down to two sequential
// requests instead of three.
async function removeLineAndSetAttributes(key, attributes) {
  await checkedFetch(window.routes.cart_update_url, { updates: { [key]: 0 }, attributes });
}

async function addLine(variantId, properties) {
  await checkedFetch(window.routes.cart_add_url, { items: [{ id: variantId, quantity: 1, properties }] });
}

// /cart/add.js also accepts sections/sections_url, same as /cart/change.js
// and /cart/update.js — so the line add and the final render can happen in
// one request instead of add-then-separately-ask-for-sections.
async function addLineAndRender(variantId, properties, generationAtStart) {
  const res = await checkedFetch(window.routes.cart_add_url, {
    items: [{ id: variantId, quantity: 1, properties }],
    ...sectionsPayload(),
  });
  renderSections(await res.json(), generationAtStart);
}

async function setAttributesAndRender(attributes, generationAtStart) {
  const res = await checkedFetch(window.routes.cart_update_url, { attributes, ...sectionsPayload() });
  renderSections(await res.json(), generationAtStart);
}

// Persisted to localStorage (not just an in-memory Map) because this data
// is effectively permanent for a given merchant — the coverage product's
// variant ladder doesn't change between page loads — so a fresh page
// navigation (PDP -> collection -> cart) shouldn't have to re-fetch it. A
// generous TTL bounds staleness risk if it's ever reconfigured; storage
// access is wrapped in try/catch since it can throw in private browsing.
const PRODUCT_CACHE_KEY = 'loopProtection:productCache';
const PRODUCT_CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours

function loadProductCache() {
  try {
    const raw = localStorage.getItem(PRODUCT_CACHE_KEY);
    if (!raw) return new Map();
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.savedAt > PRODUCT_CACHE_TTL_MS) return new Map();
    return new Map(Object.entries(parsed.products));
  } catch (err) {
    return new Map();
  }
}

function saveProductCache(cache) {
  try {
    localStorage.setItem(
      PRODUCT_CACHE_KEY,
      JSON.stringify({ savedAt: Date.now(), products: Object.fromEntries(cache) })
    );
  } catch (err) {
    // Storage full or unavailable — cache just won't survive a page reload.
  }
}

const _productCache = loadProductCache();
async function resolveVariantForAmount(handle, amount) {
  let product = _productCache.get(handle);
  if (!product) {
    product = await fetch(`/products/${handle}.js`).then((response) => response.json());
    _productCache.set(handle, product);
    saveProductCache(_productCache);
  }
  const exact = product.variants.find((variant) => variant.price === amount);
  if (exact) return exact.id;

  // This product ships with ~66 fixed price-point variants, not a
  // continuous price — confirmed live (adding a variant returns exactly
  // its nominal price, no Cart Transform Function rewrites it). A fee like
  // $3.25 has no matching variant (nearest are $2.89 / $3.29), so exact
  // cent-level pricing isn't achievable through this product as configured;
  // closing that gap for real needs a finer-grained variant added on Loop's
  // or the merchant's side. In the meantime, pick whichever neighboring
  // tier is numerically closest rather than always rounding up, so the
  // error is centered instead of consistently overcharging.
  const sorted = [...product.variants].sort((a, b) => a.price - b.price);
  let closest = sorted[0];
  for (const variant of sorted) {
    if (Math.abs(variant.price - amount) < Math.abs(closest.price - amount)) closest = variant;
  }
  return closest.id;
}

function buildAnalyzePayload(cart, productLines, region) {
  const lines = productLines.map((item) => ({
    id: String(item.key),
    productId: String(item.product_id),
    quantity: item.quantity,
    totalPrice: { amount: item.line_price, currencyCode: cart.currency },
    originalTotalPrice: { amount: item.original_line_price, currencyCode: cart.currency },
    title: item.product_title,
  }));

  const subtotalAmount = productLines.reduce((sum, item) => sum + item.line_price, 0);
  const originalSubtotalAmount = productLines.reduce((sum, item) => sum + item.original_line_price, 0);

  return {
    platform: 'shopify',
    region: region || 'US',
    cartId: cart.token,
    cart: {
      lines,
      itemCount: productLines.reduce((sum, item) => sum + item.quantity, 0),
      subtotalAmount: originalSubtotalAmount,
      totalDiscountAmount: originalSubtotalAmount - subtotalAmount,
      discountedSubtotalAmount: subtotalAmount,
      totalTaxAmount: 0,
      totalAmount: subtotalAmount,
      currencyCode: cart.currency,
    },
  };
}

// Caches the cart signature (contents sent to /v1/analyze) alongside the
// coverage variant confirmed correct for it, keyed to the cart's token, so
// a repeat sync against the same cart contents can skip calling Loop
// entirely — see the skip check in syncProtection for why all three parts
// matter. Persisted to localStorage (not just held in memory) so this
// survives a full page navigation, not just repeat triggers within one
// page load — the same $79 cart re-checked from three different pages
// shouldn't re-ask Loop three times.
const RECONCILED_KEY = 'loopProtection:lastReconciled';

function loadLastReconciled() {
  try {
    const raw = localStorage.getItem(RECONCILED_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    return null;
  }
}

function saveLastReconciled(value) {
  _lastReconciled = value;
  try {
    if (value) {
      localStorage.setItem(RECONCILED_KEY, JSON.stringify(value));
    } else {
      localStorage.removeItem(RECONCILED_KEY);
    }
  } catch (err) {
    // Storage full or unavailable — falls back to in-memory only for this page.
  }
}

let _lastReconciled = loadLastReconciled();

async function syncProtection(preloadedCart) {
  const config = window.loopConfig;
  if (!config || !config.apiKey) return;

  // Anchors this run against window.__cartRenderGeneration (bumped by
  // CartDrawer.beginRequest() in cart-drawer.js) at the moment the cart
  // snapshot below is taken. renderSections() re-checks it right before
  // painting — if cart-drawer.js has started (and by then likely finished)
  // a newer action of its own in the meantime, this run's render is
  // skipped rather than allowed to land on top of it and silently revert
  // what the user just did. The underlying cart mutation still happens
  // either way; only the DOM paint is what's skipped, and the next sync
  // (triggered by that very cart-drawer.js action) picks up the correct
  // end state.
  const generationAtStart = window.__cartRenderGeneration || 0;

  // Reuse the cart state cart-drawer.js's own mutation already fetched
  // (passed via the triggering event) when available — otherwise fall
  // back to a fresh read. getCartState() (assets/cart-drawer.js) also
  // coalesces concurrent /cart.json reads from every cart widget on the
  // page into one request instead of each firing its own.
  const cart = preloadedCart || (await getCartState());
  const productLines = cart.items.filter((item) => !isProtectionLine(item));
  const protectionLines = cart.items.filter(isProtectionLine);

  // Defensive cleanup: normally there's at most one coverage line, but a
  // race between two triggers (or two tabs open on the same cart) can
  // occasionally leave more than one behind. Clear all of them (never try
  // to reconcile which one is "right") and treat the cart as having none,
  // so the logic below adds a single fresh line back at the correct tier.
  let existingLine = null;
  let removedDuplicates = false;
  if (protectionLines.length > 1) {
    await Promise.all(protectionLines.map((line) => removeLine(line.key)));
    removedDuplicates = true;
  } else if (protectionLines.length === 1) {
    existingLine = protectionLines[0];
  }

  if (!productLines.length) {
    saveLastReconciled(null);
    if (existingLine) await removeLine(existingLine.key);
    if (existingLine || removedDuplicates) {
      const res = await checkedFetch(window.routes.cart_update_url, sectionsPayload());
      renderSections(await res.json(), generationAtStart);
    }
    return;
  }

  const currentSessionId = cart.attributes && cart.attributes._loop_session_id;
  const payload = buildAnalyzePayload(cart, productLines, config.region);
  const cartSignature = JSON.stringify(payload.cart);

  // Loop's fee is a pure function of the cart contents we send — if that's
  // byte-for-byte identical to what we last successfully reconciled *and*
  // the coverage line still has the variant that reconciliation settled
  // on, the answer can only be the same, so skip the round trip to Loop's
  // servers entirely. Checking the line's actual current variant (not just
  // the cart signature) matters if two tabs share a cart: trusting the
  // signature alone could skip re-verifying a line another tab had since
  // changed. This is the biggest lever for perceived speed, since
  // /v1/analyze is a third-party network call, unlike everything else
  // here, which is same-origin.
  if (
    existingLine &&
    _lastReconciled &&
    _lastReconciled.cartToken === cart.token &&
    _lastReconciled.cartSignature === cartSignature &&
    _lastReconciled.variantId === existingLine.id
  ) {
    return;
  }

  let data;
  // Reusing the last session id (from the cart attribute Loop itself set)
  // keeps /v1/analyze returning the *same* session when pricing hasn't
  // changed, instead of minting a new one on every call. That, in turn,
  // lets the no-op path below skip writing to the cart / re-rendering the
  // drawer on every open — otherwise every drawer open would force a full
  // section repaint even when nothing changed, reintroducing the exact
  // flash Loop's own widget used to cause.
  if (currentSessionId) payload.sessionId = currentSessionId;

  try {
    const res = await fetch(LOOP_ANALYZE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Authorization': config.apiKey,
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`Loop analyze failed: ${res.status}`);
    data = await res.json();
  } catch (err) {
    console.error('[CartProtectionSync] analyze failed:', err);
    return;
  }

  if (!data.eligible || !data.chargeInstructions || data.chargeInstructions.method !== 'product_line_item') {
    saveLastReconciled(null);
    if (existingLine) await removeLine(existingLine.key);
    return;
  }

  const amount = data.chargeInstructions.amount;
  // Clears _loop_protection_declined (set by the "Continue without free
  // returns" link — see skipProtectionAndCheckout) whenever protection is
  // confirmed present, so the flag reflects the current checkout attempt
  // rather than lingering from an earlier decline in the same cart.
  const attributes = { ...data.cartAttributes, _loop_protection_declined: '' };
  const sessionUnchanged = data.sessionId === currentSessionId;
  const variantId = await resolveVariantForAmount(data.chargeInstructions.product.handle, amount);
  saveLastReconciled({ cartToken: cart.token, cartSignature, variantId });

  // resolveVariantForAmount caches the product lookup, so calling it again
  // here is cheap after the first time — what's actually expensive, and
  // worth skipping, is the cart mutation below when the tier hasn't
  // changed. That keeps repeat syncs (most of them, once a session
  // settles) down to a single fast attribute write instead of a full
  // add/remove round trip.
  if (existingLine) {
    if (existingLine.id === variantId) {
      if (!sessionUnchanged) await setAttributesAndRender(attributes, generationAtStart);
      return; // Nothing to add/remove and nothing to write — no-op.
    }
    // Tier changed — remove the stale line, then add the new one. These
    // must be sequential, not parallel: firing both at once against the
    // same cart session races Shopify's own read-modify-write cart
    // storage and can leave *both* lines behind instead of one replacing
    // the other. The removal is combined with the attribute write (one
    // request) and the add carries sections for the final render (one
    // request) — two round trips total instead of three.
    await removeLineAndSetAttributes(existingLine.key, attributes);
    await addLineAndRender(variantId, data.chargeInstructions.lineItemProperties, generationAtStart);
    return;
  }

  await addLine(variantId, data.chargeInstructions.lineItemProperties);
  await setAttributesAndRender(attributes, generationAtStart);
}

let _syncQueue = Promise.resolve();
function enqueueSync(preloadedCart) {
  _syncQueue = _syncQueue.then(() => syncProtection(preloadedCart)).catch((err) => {
    console.error('[CartProtectionSync] sync error:', err);
  });
  return _syncQueue;
}

// Debounced on the trailing edge: several "cart:updated" events can fire in
// quick succession (e.g. holding the quantity stepper, or a quantity change
// plus the gift-policy pass it triggers), and each one re-running analyze
// against a stale intermediate cart state is both wasted work and the
// source of the fee visibly flickering through interim values. Waiting for
// things to settle means exactly one sync per burst, against the final
// state — and enqueueSync's own queue still protects against overlap if a
// burst spans a slow in-flight request.
//
// cart-drawer.js's own mutations (quantity change, gift-policy pass, kit
// update) already fetch the post-mutation cart as part of doing their job,
// and pass it along on "cart:updated" as event.detail.cart — reusing it
// here skips an entire redundant /cart.json round trip on the trigger that
// fires most often. "cart:updated" from a plain drawer open, and "cart:
// item-added" from the PDP (which only carries the added line, not the
// full cart), have no usable detail, so syncProtection falls back to
// getCartState() for those.
let _preloadedCart = null;
let _debounceTimer = null;
function debouncedSync(event) {
  if (event && event.detail && event.detail.cart) _preloadedCart = event.detail.cart;
  clearTimeout(_debounceTimer);
  _debounceTimer = setTimeout(() => {
    const cart = _preloadedCart;
    _preloadedCart = null;
    enqueueSync(cart);
  }, 80);
}

document.addEventListener('cart:updated', debouncedSync);
document.addEventListener('cart:item-added', debouncedSync);
enqueueSync();

// "Continue without free returns" — removes every coverage line (should
// normally be just one, but clears any stray duplicate too) and proceeds
// to checkout. Sequential removals, not Promise.all: concurrent writes to
// the same cart session are what caused the duplicate-line bug in the
// first place (see removeLineAndSetAttributes above).
document.addEventListener('click', (event) => {
  const link = event.target.closest('[data-skip-protection]');
  if (!link || link.getAttribute('aria-disabled') === 'true') return;
  event.preventDefault();
  skipProtectionAndCheckout(link);
});

async function skipProtectionAndCheckout(link) {
  link.setAttribute('aria-disabled', 'true');
  try {
    const cart = await getCartState();
    for (const line of cart.items.filter(isProtectionLine)) {
      await removeLine(line.key);
    }
    saveLastReconciled(null);
    // Tracking marker for declines via this link — cleared automatically
    // (see the attributes object in syncProtection) the next time
    // protection is confirmed present, so it reflects the current
    // checkout attempt rather than lingering from an earlier decline.
    await checkedFetch(window.routes.cart_update_url, {
      attributes: { _loop_protection_declined: 'true' },
    });
  } catch (err) {
    console.error('[CartProtectionSync] Failed to remove protection before checkout:', err);
  }
  window.location.href = link.href;
}
